import { z } from 'zod';
import { CLUSTER_APPLY_MAX_SUGGESTIONS, ClassificationSuggestionSchema, TrackSchema } from './classify';
import { ScanJobDataSchema } from './scan';
import { ActivityLogSourceSchema } from './activity';
import { AutomationModeSchema, AutomationPolicySummarySchema } from './preferences';

/**
 * Foundational MCP (Model Context Protocol) schemas for the remote assistant
 * surface (epic #524, story #525).
 *
 * The **outcome envelope** is the contract every MCP tool result must conform
 * to. It lets an assistant both understand the state and tell
 * the user exactly how to self-resolve, without ever exposing tokens or secrets.
 *
 *   { outcome, data?, issue? }
 *
 * `issue.code` reuses `packages/shared/src/constants/error-codes.ts` so the same
 * machine codes flow across the normal API and the assistant surface.
 *
 * Layer separation (non-negotiable):
 *  - Layer 1 (assistant -> PureBox) auth failures are transport 401/403 +
 *    `WWW-Authenticate`; they never appear inside this envelope.
 *  - Layer 2 (PureBox -> Gmail) state is reported only as non-secret `data` or a
 *    `needs_user_action`/`transient` envelope inside a 200 result, so a Gmail
 *    problem can never trigger an assistant re-link loop.
 */

/**
 * The four assistant-actionable outcomes.
 *  - `ok`               - the tool succeeded; `data` carries the result.
 *  - `needs_user_action`- the user must do something (e.g. connect Gmail); the
 *                         `issue.action_url` deep-links them to it.
 *  - `blocked`          - entitlement/policy stop (e.g. free-tier limit); not
 *                         retryable without the user changing state.
 *  - `transient`        - temporary failure (rate limit, upstream 5xx); retry.
 */
export const McpOutcomeEnum = z.enum(['ok', 'needs_user_action', 'blocked', 'transient'])
export type McpOutcome = z.infer<typeof McpOutcomeEnum>

/**
 * Structured issue attached to every non-`ok` envelope (and optionally to an
 * `ok` envelope carrying a soft hint). `remediation` is an imperative,
 * user-facing instruction that is also mirrored into the MCP tool `content`
 * text so a model that ignores `structuredContent` still narrates the fix.
 */
export const McpIssueSchema = z.object({
  /** Machine code from ErrorCodes (e.g. `GMAIL_TOKEN_EXPIRED`, `NO_PUREBOX_ACCOUNT`). */
  code: z.string().min(1),
  /** Short human-readable title. */
  title: z.string().min(1),
  /** Human-readable explanation of what happened. */
  detail: z.string().min(1),
  /** Imperative instruction telling the user exactly how to self-resolve. */
  remediation: z.string().min(1),
  /**
   * Cold-start-safe deep link the user can open to resolve the issue. Built by
   * `buildRemediationUrl` - always token-free and carries `?source=assistant`.
   */
  action_url: z.string().url().optional(),
  /** Whether retrying the same call can succeed (true for `transient`). */
  retryable: z.boolean(),
  /** Back-off hint for rate-limit/`transient` cases. */
  retry_after_seconds: z.number().int().nonnegative().optional(),
})
export type McpIssue = z.infer<typeof McpIssueSchema>

/**
 * The MCP outcome envelope. `data` is intentionally permissive at the shared
 * layer - each tool refines it with its own output schema. Invariants enforced:
 *  - non-`ok` outcomes MUST carry an `issue`.
 *  - `ok` outcomes MUST NOT carry a `blocked`-style issue but MAY omit `issue`.
 */
export const McpOutcomeEnvelopeSchema = z
  .object({
    outcome: McpOutcomeEnum,
    data: z.unknown().optional(),
    issue: McpIssueSchema.optional(),
  })
  .refine((value) => value.outcome === 'ok' || value.issue !== undefined, {
    message: "A non-'ok' outcome must include an 'issue'",
    path: ['issue'],
  })
  .refine((value) => value.outcome !== 'transient' || value.issue?.retryable === true, {
    message: "A 'transient' outcome must have issue.retryable === true",
    path: ['issue', 'retryable'],
  })
export type McpOutcomeEnvelope = z.infer<typeof McpOutcomeEnvelopeSchema>

// ---------------------------------------------------------------------------
// v1 tool data shapes
// ---------------------------------------------------------------------------

/** Best-effort assistant provider label. Unknown verified clients are labeled `other`. */
export const AssistantProviderEnum = z.enum(['chatgpt', 'claude', 'other'])
export type AssistantProvider = z.infer<typeof AssistantProviderEnum>

/**
 * Non-secret PureBox -> Gmail connection state surfaced to the assistant.
 * Never includes tokens, refresh tokens, or token-adjacent metadata.
 *  - `none`      -> use connect copy (`GMAIL_TOKEN_MISSING`)
 *  - `expired`/`revoked` -> use reconnect copy (`GMAIL_TOKEN_EXPIRED`)
 */
export const GmailConnectionStateEnum = z.enum(['none', 'connected', 'expired', 'revoked'])
export type GmailConnectionState = z.infer<typeof GmailConnectionStateEnum>

/**
 * Output of the v1 `get_purebox_connection_status` tool. No input. Read-only.
 * Contains only linking metadata and non-secret Gmail connection state.
 */
export const PureboxConnectionStatusSchema = z.object({
  /** Whether an assistant is currently linked to this PureBox account. */
  linked: z.boolean(),
  /** Assistant provider label, or null when no assistant is linked. */
  provider: AssistantProviderEnum.nullable(),
  /** ISO timestamp the assistant was linked, or null when not linked. */
  connected_at: z.string().nullable(),
  /** Connected Gmail account email, or null when unavailable/not connected. */
  gmail_email: z.string().email().nullable(),
  /** Granted MCP scopes (never Gmail OAuth scopes). */
  scopes: z.array(z.string()),
  /** ISO timestamp the link was last used, when tracked. */
  last_used_at: z.string().nullable().optional(),
  /** Non-secret PureBox -> Gmail connection state. */
  gmail_connection_state: GmailConnectionStateEnum,
})
export type PureboxConnectionStatus = z.infer<typeof PureboxConnectionStatusSchema>

// ---------------------------------------------------------------------------
// Account-side connection management (normal ts-rest API, authMiddleware)
// ---------------------------------------------------------------------------

/**
 * A single linked-assistant connection as shown in Account settings. Contains
 * only non-secret consent/audit metadata - never tokens or token hashes.
 */
export const McpAssistantConnectionSchema = z.object({
  id: z.string().uuid(),
  provider: AssistantProviderEnum,
  /** ISO timestamp the assistant was linked. */
  connected_at: z.string(),
  /** Granted MCP scopes (never Gmail OAuth scopes). */
  scopes: z.array(z.string()),
  /** ISO timestamp the link was last used, when tracked. */
  last_used_at: z.string().nullable(),
})
export type McpAssistantConnection = z.infer<typeof McpAssistantConnectionSchema>

/** GET /v1/mcp/connections - list the caller's active assistant links. */
export const ListMcpConnectionsResponseSchema = z.object({
  connections: z.array(McpAssistantConnectionSchema),
})
export type ListMcpConnectionsResponse = z.infer<typeof ListMcpConnectionsResponseSchema>

/** POST /v1/mcp/connections/:id/revoke - unlink one assistant connection. */
export const RevokeMcpConnectionResponseSchema = z.object({
  revoked: z.literal(true),
  /** 1 when newly revoked, 0 when it was already revoked/gone (idempotent). */
  revoked_count: z.number().int().min(0),
})
export type RevokeMcpConnectionResponse = z.infer<typeof RevokeMcpConnectionResponseSchema>

// ---------------------------------------------------------------------------
// MCP assistant-native Archive / Trash apply plans (#553, #559)
// ---------------------------------------------------------------------------

// Apply-plan scope selects WHICH suggestions to apply, never WHAT action to
// take. The action for each suggestion is derived server-side from that
// suggestion's own track (Archive -> archive, Trash -> trash), so a single
// plan may mix Archive and Trash suggestions.
export const McpApplyPlanScopeEnum = z.enum(['all', 'selected'])
export type McpApplyPlanScope = z.infer<typeof McpApplyPlanScopeEnum>

export const McpApplyActionEnum = z.enum(['archive', 'trash'])
export type McpApplyAction = z.infer<typeof McpApplyActionEnum>

export const McpApplyPlanStatusEnum = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED'])
export type McpApplyPlanStatus = z.infer<typeof McpApplyPlanStatusEnum>

export const PrepareApplyPlanInputSchema = z.object({
  // 'all'      -> every pending applyable (Archive or Trash) suggestion, capped.
  // 'selected' -> exactly the suggestion_ids provided.
  scope: McpApplyPlanScopeEnum,
  suggestion_ids: z
    .array(z.string().min(1).max(128))
    .min(1)
    .max(CLUSTER_APPLY_MAX_SUGGESTIONS)
    .optional(),
}).strict().superRefine((value, ctx) => {
  if (value.scope === 'selected' && !value.suggestion_ids?.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['suggestion_ids'], message: 'selected scope requires suggestion_ids' })
  }
  if (value.scope !== 'selected' && value.suggestion_ids !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['suggestion_ids'], message: 'suggestion_ids is only valid with selected scope' })
  }
})
export type PrepareApplyPlanInput = z.infer<typeof PrepareApplyPlanInputSchema>

export const SelectedApplyPlanSkippedSuggestionSchema = z.object({
  suggestion_id: z.string().min(1),
  reason: z.enum(['invalid', 'expired', 'ineligible', 'duplicate']),
  detail: z.string().min(1),
}).strict()
export type SelectedApplyPlanSkippedSuggestion = z.infer<typeof SelectedApplyPlanSkippedSuggestionSchema>

export const PrepareApplyPlanResultSchema = z.object({
  plan_id: z.string().uuid(),
  scope: McpApplyPlanScopeEnum,
  planned_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  // Per-action breakdown of the planned suggestions. A plan may mix both.
  archive_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  trash_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  max_allowed_count: z.number().int().min(1).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  expires_at: z.string().datetime(),
  skipped_suggestions: z.array(SelectedApplyPlanSkippedSuggestionSchema).max(CLUSTER_APPLY_MAX_SUGGESTIONS).optional(),
}).refine((value) => value.max_allowed_count >= value.planned_count, {
  message: 'max_allowed_count must be greater than or equal to planned_count',
  path: ['max_allowed_count'],
}).refine((value) => value.archive_count + value.trash_count === value.planned_count, {
  message: 'archive_count + trash_count must equal planned_count',
  path: ['planned_count'],
})
export type PrepareApplyPlanResult = z.infer<typeof PrepareApplyPlanResultSchema>

export const ApplyApplyPlanInputSchema = z.object({
  plan_id: z.string().uuid(),
  idempotency_key: z.string().min(8).max(200),
}).strict()
export type ApplyApplyPlanInput = z.infer<typeof ApplyApplyPlanInputSchema>

export const ApplyApplyPlanResultSchema = z.object({
  plan_id: z.string().uuid(),
  job_id: z.string().uuid().nullable(),
  operation_id: z.string().min(1),
  status: McpApplyPlanStatusEnum,
  planned_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  applied_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  failed_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  retry_after_seconds: z.number().int().positive().optional(),
}).refine((value) => value.applied_count + value.failed_count <= value.planned_count, {
  message: 'applied_count + failed_count cannot exceed planned_count',
  path: ['applied_count'],
})
export type ApplyApplyPlanResult = z.infer<typeof ApplyApplyPlanResultSchema>

export const ApplyStatusInputSchema = z.object({
  plan_id: z.string().uuid().optional(),
  job_id: z.string().uuid().optional(),
}).strict().refine((value) => Boolean(value.plan_id) !== Boolean(value.job_id), {
  message: 'Provide exactly one of plan_id or job_id',
  path: ['plan_id'],
})
export type ApplyStatusInput = z.infer<typeof ApplyStatusInputSchema>

export const ApplyStatusResultSchema = z.object({
  plan_id: z.string().uuid(),
  job_id: z.string().uuid().nullable(),
  operation_id: z.string().min(1).nullable(),
  status: McpApplyPlanStatusEnum,
  planned_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  applied_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  failed_count: z.number().int().min(0).max(CLUSTER_APPLY_MAX_SUGGESTIONS),
  retryable: z.boolean(),
  retry_after_seconds: z.number().int().positive().optional(),
  guidance: z.string().min(1),
}).refine((value) => value.applied_count + value.failed_count <= value.planned_count, {
  message: 'applied_count + failed_count cannot exceed planned_count',
  path: ['applied_count'],
})
export type ApplyStatusResult = z.infer<typeof ApplyStatusResultSchema>

// ---------------------------------------------------------------------------
// MCP suggestion stats tool (story #552)
// ---------------------------------------------------------------------------

export const PureboxSuggestionStatsInputSchema = z.object({
  status: z.enum(['pending', 'applied', 'rejected']).optional(),
  track: TrackSchema.optional(),
  label: z.string().min(1).max(120).optional(),
}).strict()
export type PureboxSuggestionStatsInput = z.infer<typeof PureboxSuggestionStatsInputSchema>

const PureboxSuggestionStatsLabelAggregateSchema = z.object({
  count: z.number().int().nonnegative(),
  size_bytes: z.number().nonnegative(),
}).strict()

const PureboxSuggestionStatsApplyOptionSchema = z.object({
  action: z.enum(['review_in_purebox', 'apply_archive_suggestions', 'apply_trash_suggestions']),
  label: z.string().min(1),
  description: z.string().min(1),
}).strict()

export const PureboxSuggestionStatsSchema = z.object({
  by_track: z.record(z.string(), z.number().int().nonnegative()),
  size_by_track: z.record(z.string(), z.number().nonnegative()),
  reclaimable_seconds_by_track: z.record(z.string(), z.number().int().nonnegative()),
  by_label: z.record(z.string(), z.record(z.string(), PureboxSuggestionStatsLabelAggregateSchema)),
  apply_options: z.array(PureboxSuggestionStatsApplyOptionSchema).min(1).max(5),
}).strict()
export type PureboxSuggestionStats = z.infer<typeof PureboxSuggestionStatsSchema>

// ---------------------------------------------------------------------------
// Assistant feedback tool (MCP-only, story #554)
// ---------------------------------------------------------------------------

export const AssistantFeedbackTypeEnum = z.enum(['bug', 'confusion', 'feature', 'other'])
export type AssistantFeedbackType = z.infer<typeof AssistantFeedbackTypeEnum>

export const AssistantFeedbackSeverityEnum = z.enum(['low', 'medium', 'high'])
export type AssistantFeedbackSeverity = z.infer<typeof AssistantFeedbackSeverityEnum>

export const SendAssistantFeedbackInputSchema = z
  .object({
    feedback_text: z.string().min(1).max(5000),
    feedback_type: AssistantFeedbackTypeEnum,
    severity: AssistantFeedbackSeverityEnum,
    related_ids: z
      .object({
        plan_id: z.string().min(1).max(128).optional(),
        job_id: z.string().min(1).max(128).optional(),
        operation_id: z.string().min(1).max(128).optional(),
      })
      .strict()
      .optional(),
    assistant_observation: z.string().max(2000).optional(),
  })
  .strict()
export type SendAssistantFeedbackInput = z.infer<typeof SendAssistantFeedbackInputSchema>

export const SendAssistantFeedbackResultSchema = z
  .object({
    id: z.string().min(1),
    status: z.enum(['sent', 'queued', 'duplicate', 'rate_limited', 'rejected']),
  })
  .strict()
export type SendAssistantFeedbackResult = z.infer<typeof SendAssistantFeedbackResultSchema>

// ---------------------------------------------------------------------------
// MCP bounded suggestion list + selected apply (story #555)
// ---------------------------------------------------------------------------

export const PureboxListSuggestionsInputSchema = z.object({
  status: z.enum(['pending', 'applied', 'rejected']).optional(),
  track: TrackSchema.optional(),
  search: z.string().optional(),
  label: z.string().min(1).max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional(),
  offset: z.coerce.number().int().min(0).default(0).optional(),
}).strict()
export type PureboxListSuggestionsInput = z.infer<typeof PureboxListSuggestionsInputSchema>

export const PureboxListSuggestionPlannedActionSchema = z.enum([
  'archive',
  'trash',
  'keep_unread',
  'move_to_inbox',
  'none',
])
export type PureboxListSuggestionPlannedAction = z.infer<typeof PureboxListSuggestionPlannedActionSchema>

export const PureboxListSuggestionItemSchema = z.object({
  suggestion_id: z.string().min(1),
  track: TrackSchema,
  labels: z.array(z.string()),
  planned_action: PureboxListSuggestionPlannedActionSchema,
  received_at: z.string().datetime().nullable(),
  size: z.number().int().nonnegative().nullable(),
  sender: z.string().nullable(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  rationale: z.string(),
}).strict()
export type PureboxListSuggestionItem = z.infer<typeof PureboxListSuggestionItemSchema>

export const PureboxListSuggestionsResultSchema = z.object({
  suggestions: z.array(PureboxListSuggestionItemSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().min(1).max(100),
  offset: z.number().int().min(0),
  has_more: z.boolean(),
  summary: z.unknown().optional(),
}).strict()
export type PureboxListSuggestionsResult = z.infer<typeof PureboxListSuggestionsResultSchema>

// ---------------------------------------------------------------------------
// MCP sender pattern tools (#557)
// ---------------------------------------------------------------------------

const McpPendingByTrackSchema = z.object({
  Attention: z.number().int().nonnegative(),
  Archive: z.number().int().nonnegative(),
  Trash: z.number().int().nonnegative(),
}).strict()

export const PureboxSenderSearchInputSchema = z.object({
  query: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(100).default(50).optional(),
}).strict()
export type PureboxSenderSearchInput = z.infer<typeof PureboxSenderSearchInputSchema>

export const PureboxSenderCandidateSchema = z.object({
  id: z.string().uuid(),
  sender_email: z.string(),
  sender_display_name: z.string().nullable(),
  sender_domain: z.string(),
  pending_by_track: McpPendingByTrackSchema,
  pending_total: z.number().int().nonnegative(),
  last_seen_at: z.string().nullable(),
  size_bytes: z.number().int().nonnegative(),
}).strict()
export type PureboxSenderCandidate = z.infer<typeof PureboxSenderCandidateSchema>

export const PureboxSenderSearchResultSchema = z.object({
  query: z.string(),
  result_count: z.number().int().nonnegative(),
  limit: z.number().int().min(1).max(100),
  senders: z.array(PureboxSenderCandidateSchema),
}).strict()
export type PureboxSenderSearchResult = z.infer<typeof PureboxSenderSearchResultSchema>

export const PureboxSenderBriefingInputSchema = z.object({
  // Read-only lookup keyed by the sender's email address - the natural,
  // human-readable identifier the assistant already has (no prior
  // search_purebox_senders round-trip required). The lookup stays scoped to
  // the caller's own senders (RLS by user_id), so email is safe here even
  // though the *mutating* apply-plan still requires a server-validated id.
  sender_email: z.string().trim().min(3).max(320),
  include_messages: z.boolean().default(true).optional(),
  limit: z.number().int().min(1).max(100).default(50).optional(),
  offset: z.number().int().min(0).default(0).optional(),
}).strict()
export type PureboxSenderBriefingInput = z.infer<typeof PureboxSenderBriefingInputSchema>

export const PureboxSenderInsightSchema = PureboxSenderCandidateSchema.extend({
  total_emails_received: z.number().int().nonnegative(),
  open_rate: z.number().min(0).max(1).nullable(),
  archive_rate: z.number().min(0).max(1).nullable(),
  trash_rate: z.number().min(0).max(1).nullable(),
  reply_rate: z.number().min(0).max(1).nullable(),
  is_in_contacts: z.boolean(),
  has_prior_reply_thread: z.boolean(),
  first_seen_at: z.string().nullable(),
  last_updated_at: z.string().nullable(),
  last_recalculated_at: z.string().nullable(),
}).strict()
export type PureboxSenderInsight = z.infer<typeof PureboxSenderInsightSchema>

export const PureboxSenderMessageSchema = z.object({
  suggestion_id: z.string(),
  message_id: z.string(),
  subject: z.string().nullable(),
  sender_email: z.string().nullable(),
  received_at: z.string().nullable(),
  size_bytes: z.number().int().nonnegative(),
  track: TrackSchema,
  labels: z.array(z.string()),
  rationale: z.string(),
  confidence: z.number().min(0).max(1),
  display_confidence: z.number(),
  status: z.enum(['pending', 'applied', 'rejected', 'expired']).default('pending'),
}).strict()
export type PureboxSenderMessage = z.infer<typeof PureboxSenderMessageSchema>

export const PureboxSenderBriefingSchema = z.object({
  sender: PureboxSenderInsightSchema,
  messages: z.array(PureboxSenderMessageSchema),
  result_count: z.number().int().nonnegative(),
  limit: z.number().int().min(1).max(100),
  offset: z.number().int().nonnegative(),
}).strict()
export type PureboxSenderBriefing = z.infer<typeof PureboxSenderBriefingSchema>

// ---------------------------------------------------------------------------
// MCP assistant-native scan start and progress (story #556)
// ---------------------------------------------------------------------------

export const PureboxScanStateEnum = z.enum([
  'queued',
  'running',
  'completed',
  'failed',
  'blocked',
])
export type PureboxScanState = z.infer<typeof PureboxScanStateEnum>

export const PureboxScanStatusStateEnum = z.enum([
  'not_started',
  'queued',
  'running',
  'completed',
  'failed',
  'blocked',
]);
export type PureboxScanStatusState = z.infer<typeof PureboxScanStatusStateEnum>;

export const StartPureboxScanInputSchema = z.object({}).strict()
export type StartPureboxScanInput = z.infer<typeof StartPureboxScanInputSchema>

export const PureboxScanProgressSchema = z
  .object({
    emails_listed: z.number().int().nonnegative(),
    emails_fetched: z.number().int().nonnegative(),
    emails_classified: z.number().int().nonnegative(),
    scanned_emails_size_bytes: z.number().int().nonnegative(),
    track_counts: z
      .object({
        attention: z.number().int().nonnegative(),
        archive: z.number().int().nonnegative(),
        trash: z.number().int().nonnegative(),
      })
      .nullable(),
    total_suggestions: z.number().int().nonnegative().nullable(),
  })
  .strict()
export type PureboxScanProgress = z.infer<typeof PureboxScanProgressSchema>

export const PureboxScanApplyOptionSchema = z
  .object({
    id: z.enum(['review_in_purebox', 'prepare_archive_plan']),
    label: z.string().min(1),
    description: z.string().min(1),
    tool_name: z.string().min(1).optional(),
  })
  .strict()
export type PureboxScanApplyOption = z.infer<typeof PureboxScanApplyOptionSchema>

export const StartPureboxScanResultSchema = z
  .object({
    job_id: z.string().uuid(),
    state: PureboxScanStateEnum,
    status: ScanJobDataSchema.shape.status,
    scan_mode: ScanJobDataSchema.shape.scan_mode,
    message: z.string().min(1),
    retry_after_seconds: z.number().int().positive(),
  })
  .strict()
export type StartPureboxScanResult = z.infer<typeof StartPureboxScanResultSchema>

export const GetPureboxScanStatusInputSchema = z
  .object({
    scan_job_id: z.string().uuid().optional(),
  })
  .strict()
export type GetPureboxScanStatusInput = z.infer<typeof GetPureboxScanStatusInputSchema>

export const GetPureboxScanStatusResultSchema = z
  .object({
    state: PureboxScanStatusStateEnum,
    retry_after_seconds: z.number().int().positive().optional(),
    guidance: z.string().min(1),
    scan: ScanJobDataSchema.nullable(),
    progress: PureboxScanProgressSchema,
    apply_options: z.array(PureboxScanApplyOptionSchema),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.state !== 'not_started') {
      return;
    }
    if (value.scan !== null) {
      context.addIssue({ code: 'custom', path: ['scan'], message: 'Not-started status cannot include a scan job.' });
    }
    if (value.retry_after_seconds !== undefined) {
      context.addIssue({ code: 'custom', path: ['retry_after_seconds'], message: 'Not-started status cannot include retry guidance.' });
    }
    if (value.apply_options.length > 0) {
      context.addIssue({ code: 'custom', path: ['apply_options'], message: 'Not-started status cannot include apply options.' });
    }
    const { emails_listed, emails_fetched, emails_classified, scanned_emails_size_bytes, track_counts, total_suggestions } = value.progress;
    if (emails_listed !== 0 || emails_fetched !== 0 || emails_classified !== 0 || scanned_emails_size_bytes !== 0 || track_counts !== null || total_suggestions !== null) {
      context.addIssue({ code: 'custom', path: ['progress'], message: 'Not-started status must report zero progress.' });
    }
  })
export type GetPureboxScanStatusResult = z.infer<typeof GetPureboxScanStatusResultSchema>

// ---------------------------------------------------------------------------
// MCP activity summary tool (story #558)
// ---------------------------------------------------------------------------

const ActivitySummaryIsoStringSchema = z.string().refine((value) => {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed)
}, 'Must be a valid ISO date/time')

export const PureboxActivitySummaryInputSchema = z
  .object({
    limit: z.number().int().min(1).max(100).optional().default(100),
    from: ActivitySummaryIsoStringSchema.optional(),
    to: ActivitySummaryIsoStringSchema.optional(),
    kind: z.enum(['parent', 'category', 'custom']).optional(),
    source: ActivityLogSourceSchema.optional().default('all'),
  })
  .strict()
  .refine((value) => {
    if (!value.from || !value.to) {
      return true
    }
    return Date.parse(value.from) <= Date.parse(value.to)
  }, {
    message: 'from must be before or equal to to',
    path: ['from'],
  })
export type PureboxActivitySummaryInput = z.infer<typeof PureboxActivitySummaryInputSchema>

export const PureboxActivitySummaryActionSchema = z.record(z.string(), z.unknown())
export type PureboxActivitySummaryAction = z.infer<typeof PureboxActivitySummaryActionSchema>

export const PureboxActivitySummaryItemSchema = z
  .object({
    id: z.string().min(1),
    kind: z.string().min(1),
    source: ActivityLogSourceSchema.exclude(['all']).nullable(),
    suggestion_id: z.string().nullable(),
    email_ids: z.array(z.string()),
    gmail_message_ids: z.array(z.string()),
    categories: z.array(z.string()),
    actions: z.array(PureboxActivitySummaryActionSchema),
    action_types: z.array(z.string()),
    action_summary: z.string().min(1),
    label: z.string().nullable(),
    applied: z.boolean(),
    updated_count: z.number().int().nonnegative(),
    applied_at: z.string(),
    operation_id: z.string().nullable(),
    plan_id: z.string().nullable(),
    job_id: z.string().nullable(),
    subject: z.string().nullable(),
    snippet: z.string().nullable(),
    sender_name: z.string().nullable(),
    sender_email: z.string().nullable(),
    activity_details: z.record(z.string(), z.unknown()),
  })
  .strict()
export type PureboxActivitySummaryItem = z.infer<typeof PureboxActivitySummaryItemSchema>

export const PureboxActivityRecoveryGuidanceSchema = z
  .object({
    archive: z.string().min(1),
    trash: z.string().min(1),
    limitation: z.string().min(1),
  })
  .strict()
export type PureboxActivityRecoveryGuidance = z.infer<typeof PureboxActivityRecoveryGuidanceSchema>

export const PureboxActivitySummarySchema = z
  .object({
    range: z
      .object({
        from: z.string().nullable(),
        to: z.string().nullable(),
        limit: z.number().int().min(1).max(100),
        kind: z.enum(['parent', 'category', 'custom']).nullable(),
        source: ActivityLogSourceSchema,
      })
      .strict(),
    total: z.number().int().nonnegative(),
    returned_count: z.number().int().nonnegative(),
    counts: z
      .object({
        by_action_type: z.record(z.string(), z.number().int().nonnegative()),
        by_kind: z.record(z.string(), z.number().int().nonnegative()),
        by_source: z.record(z.string(), z.number().int().nonnegative()),
        updated_count: z.number().int().nonnegative(),
      })
      .strict(),
    last_actions_by_type: z.record(z.string(), PureboxActivitySummaryItemSchema),
    items: z.array(PureboxActivitySummaryItemSchema),
    recovery_guidance: PureboxActivityRecoveryGuidanceSchema,
  })
  .strict()
export type PureboxActivitySummary = z.infer<typeof PureboxActivitySummarySchema>

// ---------------------------------------------------------------------------
// MCP automation candidates + enablement (story #560)
// ---------------------------------------------------------------------------

export const McpAutomationCandidatePatternIdEnum = z.enum([
  'auto_apply',
  'auto_apply_attention',
  'auto_apply_archive',
  'auto_apply_trash',
])
export type McpAutomationCandidatePatternId = z.infer<typeof McpAutomationCandidatePatternIdEnum>

export const McpAutomationEntitlementStateEnum = z.enum([
  'enabled',
  'eligible',
  'consent_missing',
  'not_entitled',
])
export type McpAutomationEntitlementState = z.infer<typeof McpAutomationEntitlementStateEnum>

export const GetAutomationCandidatesInputSchema = z.object({}).strict()
export type GetAutomationCandidatesInput = z.infer<typeof GetAutomationCandidatesInputSchema>

export const EnableAutomationInputSchema = z
  .object({
    mode: AutomationModeSchema.default('auto_apply'),
    candidate_pattern_id: McpAutomationCandidatePatternIdEnum.optional(),
  })
  .strict()
export type EnableAutomationInput = z.infer<typeof EnableAutomationInputSchema>

const McpAutomationEntitlementSchema = z
  .object({
    state: McpAutomationEntitlementStateEnum,
    is_eligible: z.boolean(),
    subscription_status: z.string(),
    current_mode: AutomationModeSchema,
    consent_at: z.string().datetime().nullable(),
    action_url: z.string().url().optional(),
  })
  .strict()

const McpAutomationTrackPolicySchema = z
  .object({
    min_confidence: z.number().min(0).max(1).optional(),
    description: z.string().min(1),
  })
  .strict()

const McpAutomationCandidateSchema = z
  .object({
    candidate_pattern_id: McpAutomationCandidatePatternIdEnum,
    mode_id: AutomationModeSchema,
    track: TrackSchema,
    action_type: z.enum(['label_attention', 'archive', 'trash']),
    estimated_time_impact_seconds: z.number().int().nonnegative(),
    rationale: z.string().min(1),
    policy: McpAutomationTrackPolicySchema,
    entitlement_state: McpAutomationEntitlementSchema,
    matching_suggestion_count: z.number().int().nonnegative(),
    sample_suggestions: z.array(ClassificationSuggestionSchema),
  })
  .strict()

export const AutomationCandidatesResultSchema = z
  .object({
    mode: AutomationModeSchema,
    consent_at: z.string().datetime().nullable(),
    is_eligible: z.boolean(),
    policy_summary: AutomationPolicySummarySchema,
    candidates: z.array(McpAutomationCandidateSchema),
  })
  .strict()
export type AutomationCandidatesResult = z.infer<typeof AutomationCandidatesResultSchema>

export const EnableAutomationResultSchema = z
  .object({
    mode: AutomationModeSchema,
    consent_at: z.string().datetime().nullable(),
    is_eligible: z.boolean(),
    policy_summary: AutomationPolicySummarySchema,
  })
  .strict()
export type EnableAutomationResult = z.infer<typeof EnableAutomationResultSchema>
